/**
 * Invitation landing for https://join.pebla.app/i/<opaque-token>
 *
 * - Resolves via GET /api/invitations/:token?platform=web
 * - Persists token for deferred install recovery (localStorage + Play referrer)
 * - Attempts app open (custom scheme); Universal/App Links when OS is configured
 * - Platform-aware store buttons (UA presentation only — never for auth)
 * - Does not claim/decline; never shows full phone or account existence
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { downloadLinks, JOIN_HOST } from '../../constants/links';
import { Button } from '../shared/Button';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  'https://pebla-backend-production.up.railway.app';

const PENDING_KEY = 'pebla_pending_invite_token';
const APP_SCHEME_PREFIX = 'pebla://i/';

type Preview = {
  type: string;
  status: string;
  purpose: string;
  destination: string;
  inviterDisplayName: string;
  isTargeted: boolean;
  phoneHint: string | null;
  requiresLockedPhoneOtp: boolean;
  canAccept: boolean;
  canDecline: boolean;
  expiresAt: string;
};

type FailureCategory =
  | 'malformed_token'
  | 'unknown_token'
  | 'expired'
  | 'revoked'
  | 'already_claimed'
  | 'already_declined'
  | 'backend_unavailable';

type Platform = 'ios' | 'android' | 'unknown';

function detectPlatform(): Platform {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'unknown';
}

/** Coarse UA check for common in-app browsers where store/app opens often fail. */
function isInAppBrowser(): boolean {
  const ua = navigator.userAgent || '';
  return (
    /FBAN|FBAV|Instagram|WhatsApp|Line\/|MicroMessenger|Twitter|LinkedInApp|Snapchat|musical_ly|BytedanceWebview|TikTok/i.test(
      ua
    ) ||
    /; wv\)/i.test(ua)
  );
}

function failureCopy(category: FailureCategory): {
  title: string;
  body: string;
  askSender: boolean;
  canRetry: boolean;
} {
  switch (category) {
    case 'expired':
      return {
        title: 'Invitation expired',
        body: 'Ask the sender for a new invitation link.',
        askSender: true,
        canRetry: false,
      };
    case 'revoked':
      return {
        title: 'Invitation revoked',
        body: 'This invitation is no longer available. Ask the sender for a new one if you still need access.',
        askSender: true,
        canRetry: false,
      };
    case 'already_claimed':
      return {
        title: 'Already used',
        body: 'This invitation has already been claimed. Open Pebla to continue.',
        askSender: false,
        canRetry: false,
      };
    case 'already_declined':
      return {
        title: 'Invitation declined',
        body: 'This invitation was declined and cannot be used.',
        askSender: true,
        canRetry: false,
      };
    case 'unknown_token':
    case 'malformed_token':
      return {
        title: 'Invalid invitation',
        body: 'This link looks incorrect or incomplete. Ask the sender for a new invite.',
        askSender: true,
        canRetry: false,
      };
    default:
      return {
        title: 'Something went wrong',
        body: 'We could not load this invitation. Check your connection and try again.',
        askSender: false,
        canRetry: true,
      };
  }
}

function persistPendingInvite(token: string): void {
  try {
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ token, receivedAt: Date.now(), source: 'landing' })
    );
  } catch {
    /* ignore quota / private mode */
  }
}

function playStoreUrlWithReferrer(token: string): string {
  const separator = downloadLinks.playStore.includes('?') ? '&' : '?';
  return `${downloadLinks.playStore}${separator}referrer=${encodeURIComponent(
    `invite=${token}`
  )}`;
}

function attemptOpenApp(token: string): void {
  const schemeUrl = `${APP_SCHEME_PREFIX}${encodeURIComponent(token)}`;
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = schemeUrl;
    document.body.appendChild(iframe);
    window.setTimeout(() => {
      iframe.remove();
    }, 1500);
  } catch {
    /* ignore */
  }
}

export const InvitationLanding: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const platform = useMemo(() => detectPlatform(), []);
  const inAppBrowser = useMemo(() => isInAppBrowser(), []);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [failure, setFailure] = useState<FailureCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      if (!token || token.length < 16) {
        if (!cancelled) {
          setFailure('malformed_token');
          setPreview(null);
          setLoading(false);
        }
        return;
      }

      persistPendingInvite(token);
      if (!cancelled) {
        setLoading(true);
        setFailure(null);
        setPreview(null);
      }

      try {
        const res = await fetch(
          `${API_BASE}/api/invitations/${encodeURIComponent(token)}?platform=web`,
          { credentials: 'include', signal: controller.signal }
        );
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!res.ok) {
          setFailure((body?.error as FailureCategory) || 'backend_unavailable');
          setLoading(false);
          return;
        }

        setPreview(body.data as Preview);
        setLoading(false);
        attemptOpenApp(token);
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) {
          return;
        }
        setFailure('backend_unavailable');
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token, reloadKey]);

  const playUrl = playStoreUrlWithReferrer(token);
  const schemeHref = `${APP_SCHEME_PREFIX}${encodeURIComponent(token)}`;
  const universalHref = `https://${JOIN_HOST}/i/${encodeURIComponent(token)}`;

  const handleRetry = () => setReloadKey((k) => k + 1);

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <img src="/images/icons/pe.svg" alt="Pebla" style={styles.logo} />
      </header>

      {inAppBrowser && (
        <div style={styles.inAppBanner} role="status">
          <p style={styles.inAppTitle}>Open in your browser</p>
          <p style={styles.inAppBody}>
            In-app browsers (WhatsApp, Instagram, and similar) often block opening
            the Pebla app or store. Tap the menu (⋯) and choose{' '}
            <strong>Open in Browser</strong>, Safari, or Chrome — then use the
            buttons below.
          </p>
          <div style={styles.inAppActions}>
            <a href={universalHref} style={styles.inAppLink}>
              Open in browser
            </a>
            {(platform === 'ios' || platform === 'unknown') && (
              <a
                href={downloadLinks.appStore}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.inAppLink}
              >
                App Store
              </a>
            )}
            {(platform === 'android' || platform === 'unknown') && (
              <a
                href={playUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.inAppLink}
              >
                Play Store
              </a>
            )}
          </div>
        </div>
      )}

      <main style={styles.main}>
        {loading && (
          <p style={styles.muted}>Opening invitation…</p>
        )}

        {!loading && failure && (
          <FailureView
            category={failure}
            platform={platform}
            playUrl={playUrl}
            schemeHref={schemeHref}
            onRetry={handleRetry}
          />
        )}

        {!loading && !failure && preview && (
          <>
            <p style={styles.eyebrow}>Pebla invitation</p>
            <h1 style={styles.title}>
              {preview.inviterDisplayName} invited you
            </h1>
            <p style={styles.body}>{preview.purpose}</p>
            {preview.phoneHint ? (
              <p style={styles.hint}>For number ending {preview.phoneHint}</p>
            ) : null}

            <a href={schemeHref} style={styles.primary}>
              Open in Pebla
            </a>

            <StoreButtons platform={platform} playUrl={playUrl} />

            <p style={styles.footer}>
              Already installed? Open Pebla manually — we will restore this
              invitation.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

const FailureView: React.FC<{
  category: FailureCategory;
  platform: Platform;
  playUrl: string;
  schemeHref: string;
  onRetry: () => void;
}> = ({ category, platform, playUrl, schemeHref, onRetry }) => {
  const copy = failureCopy(category);

  return (
    <>
      <h1 style={styles.title}>{copy.title}</h1>
      <p style={styles.body}>{copy.body}</p>
      {copy.askSender && (
        <p style={styles.hint}>Ask the sender for a new invite if you need one.</p>
      )}

      {copy.canRetry ? (
        <div style={styles.retryWrap}>
          <Button onClick={onRetry}>Try again</Button>
        </div>
      ) : (
        <a href={schemeHref} style={styles.primary}>
          Open Pebla
        </a>
      )}

      <StoreButtons platform={platform} playUrl={playUrl} />

      <p style={styles.footer}>Get the app, then open Pebla to continue.</p>
    </>
  );
};

const StoreButtons: React.FC<{
  platform: Platform;
  playUrl: string;
}> = ({ platform, playUrl }) => {
  const showIos = platform === 'ios' || platform === 'unknown';
  const showAndroid = platform === 'android' || platform === 'unknown';

  return (
    <div style={styles.stores}>
      {showIos && (
        <a
          href={downloadLinks.appStore}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.storeLink}
        >
          <img
            src="/images/download/app-store.svg"
            alt="Download on the App Store"
            style={styles.storeBadge}
          />
        </a>
      )}
      {showAndroid && (
        <a
          href={playUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.storeLink}
        >
          <img
            src="/images/download/play-store.svg"
            alt="Get it on Google Play"
            style={styles.storeBadge}
          />
        </a>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    maxWidth: 420,
    margin: '0 auto',
    padding: '20px 16px 40px',
    boxSizing: 'border-box',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    color: '#111827',
    background:
      'radial-gradient(ellipse at top, #e8f5ef 0%, #f9fafb 42%, #ffffff 100%)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 16,
  },
  logo: { width: 40, height: 40 },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  inAppBanner: {
    backgroundColor: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: 12,
    padding: '14px 16px',
    marginBottom: 20,
  },
  inAppTitle: {
    margin: '0 0 6px',
    fontSize: 15,
    fontWeight: 700,
    color: '#9a3412',
  },
  inAppBody: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.45,
    color: '#7c2d12',
  },
  inAppActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
  },
  inAppLink: {
    fontSize: 13,
    fontWeight: 600,
    color: '#c2410c',
    textDecoration: 'underline',
  },
  eyebrow: {
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontSize: 12,
    fontWeight: 600,
    color: '#3d6b5a',
    margin: 0,
  },
  title: {
    fontSize: 28,
    lineHeight: 1.2,
    fontWeight: 700,
    margin: '8px 0 12px',
    color: '#111827',
  },
  body: {
    fontSize: 16,
    lineHeight: 1.5,
    color: '#374151',
    margin: 0,
  },
  hint: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 10,
    marginBottom: 0,
  },
  muted: {
    color: '#6b7280',
    fontSize: 16,
    fontWeight: 500,
    textAlign: 'center',
  },
  primary: {
    display: 'block',
    marginTop: 28,
    textAlign: 'center',
    background: '#1f6b4f',
    color: '#fff',
    padding: '16px 18px',
    borderRadius: 8,
    textDecoration: 'none',
    fontWeight: 700,
    fontSize: 16,
  },
  retryWrap: {
    marginTop: 28,
  },
  stores: {
    display: 'flex',
    gap: 12,
    marginTop: 20,
  },
  storeLink: {
    flex: 1,
    display: 'block',
  },
  storeBadge: {
    width: '100%',
    height: 'auto',
    cursor: 'pointer',
  },
  footer: {
    marginTop: 28,
    fontSize: 13,
    lineHeight: 1.45,
    color: '#6b7280',
  },
};

export default InvitationLanding;
